/**
 * Fundraising campaigns — «Innsamling».  MKK module 21 (owner 2026-10-29).
 *
 * A campaign is a goal the mosque sets for giving: a title, a few words, an Islamic purpose (zakat, sadaqa, fitrana,
 * qurbani, the mosque and its building, langar, other), a goal in whole øre and the days it runs. Staff write it as a
 * draft, make it active, and close it. Closed is final: a closed campaign is read-only, for ever.
 *
 * PROGRESS IS NEVER STORED. Money records are append-only and live where the money is: a Vipps gift is a `donation`
 * row, and a gift given to a campaign carries the campaign's id (donation.campaign_id, set once when the gift starts).
 * What a campaign has raised is computed every time from those rows (tally), plus the totals other kinds of gift hand
 * in through `external` (cash gifts, built elsewhere: [{ campaignId, amountOre, count, month?, source }]). A gift
 * counts once it is CAPTURED, net of what the mosque refunded — the same rule as the treasurer's «Mottatt» (donations
 * list) and vipps-payment.shownAmountNok, here in øre.
 *
 * NO DONOR, EVER. Nothing here takes or returns a name, a member, an account or a reference: a total and a count.
 *
 * Each campaign purpose maps to the gift purpose the payment carries (donations.PURPOSES): zakat → zakat,
 * sadaqa → sadaqah, qurbani → qurbani, mosque → building, fitrana → fitrana and langar → langar (gift purposes of their
 * own since 2026-10-29, owner); only «other» gives as «general» — the campaign link says what the gift was for.
 *
 * PUBLIC: running campaigns, and — owner 2026-10-29 — a CLOSED one's final total («Samlet inn …»): closed within the
 * last CLOSED_PUBLIC_DAYS days for the lists (app, web), CLOSED_SCREEN_DAYS for the screens. A closed campaign is only
 * public when it had begun by the day it was closed and something came in (`wasLive`): a draft closed before anybody
 * saw it stays unseen. Drafts never; an active one past its days but not yet closed («ended») is not shown either.
 *
 * TELETHON (owner 2026-10-31, migration 081). Each campaign has a public page, `<base>/v1/gi/<slug>` (pageUrl): the slug
 * is made ONCE from the title (toSlug, as MKK's portal does; uniqueSlug adds -2, -3 …) and never changes afterwards —
 * it is printed on posters and drawn as a QR on the wall. Two switches: `live` («Telethon-modus»: the wall stays on the
 * campaign slide, with a slim prayer-time strip, a «Siste gaver» ticker and a thank-you flash when the total grows) and
 * `showDonors` («Vis givere som vil vises»). Telethon mode is for ONE day (PM 2026-10-31): switching it on stores the
 * mosque-local day (`liveOn`), and it is honoured only on that day (liveToday) — at midnight the walls rotate again,
 * though `live` stays as staff set it. THE ONE EXCEPTION to «no donor, ever»: a giver who ticked «Vis fornavnet mitt»
 * (donation.show_name, the first name they typed in donation.shown_name) is listed as that first name and the amount;
 * a gift whose giver did not is never listed on its own (PM 2026-10-31) — it only adds to the total (recentGifts).
 * Never a surname, never a phone, never a member — and only when the mosque switched it on.
 *
 * Pure: no I/O, no clock — "today" (mosque-local, tz.dateIn) and the zone are passed in.
 */
const Day = require('./day');
const tz = require('./tz');
const { oneLine, text, isId } = require('./tasks');
const Csv = require('./csv');
const { formatKr, kr } = require('./money');

const PURPOSES = ['zakat', 'sadaqa', 'fitrana', 'qurbani', 'mosque', 'langar', 'other'];
const PURPOSE_NO = { zakat: 'Zakat', sadaqa: 'Sadaqa', fitrana: 'Fitrana', qurbani: 'Qurbani', mosque: 'Moské og bygg', langar: 'Langar', other: 'Annet' };
/** The gift purpose (donations.PURPOSES) a campaign's gifts are given under. */
const DONATION_PURPOSE = { zakat: 'zakat', sadaqa: 'sadaqah', fitrana: 'fitrana', qurbani: 'qurbani', mosque: 'building', langar: 'langar', other: 'general' };
/** How long a closed campaign's final total stays in the public list (GET /v1/campaigns, the app): about a quarter. */
const CLOSED_PUBLIC_DAYS = 90;
/** … and on the screens: a fortnight. A wall that thanks the congregation for three months stops being read. */
const CLOSED_SCREEN_DAYS = 14;
const STATUSES = ['draft', 'active', 'closed'];
const STATUS_NO = { draft: 'Utkast', active: 'Aktiv', closed: 'Avsluttet' };
/** What staff may set by editing. Closing has its own route, because it is final. */
const SETTABLE_STATUSES = ['draft', 'active'];
/** Where an active campaign is, by the mosque's calendar. */
const PHASE_NO = { draft: 'Utkast', scheduled: 'Starter senere', running: 'Pågår', ended: 'Perioden er over', closed: 'Avsluttet' };
const MAX_TITLE = 80;
const MAX_TEXT = 600;
/** A goal is whole øre: at least 1 kr, at most 100 million kr. */
const MAX_GOAL_ORE = 100_000_000_00;
/** At most two years from the first day to the last. */
const MAX_DAYS = 731;
const MAX_CAMPAIGNS = 1000;
// 076 (Arrangementer pro): `eventId` — the event this campaign is for (an id or null; that it exists is the API's check).
const FIELDS = ['title', 'text', 'purpose', 'goalOre', 'startsOn', 'endsOn', 'status', 'eventId'];
/** The telethon switches (2026-10-31): booleans, off unless sent; an edit keeps what the campaign has. */
const FLAGS = ['live', 'showDonors'];
/** Telethon mode holds the wall only on the mosque-local day it was switched on (PM 2026-10-31). */
const liveToday = (c, today) => Boolean(c && c.live && c.liveOn && Day.valid(today) && c.liveOn === today);
const FLAG_NO = { live: 'Telethon-modus er på (true) eller av (false).', showDonors: 'Visning av givere er på (true) eller av (false).' };
/** A slug: lower-case letters, digits and single hyphens, at most MAX_SLUG — a page address that fits a QR. */
const MAX_SLUG = 48;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/**
 * The first name a giver chose to show (review 2026-10-31): it goes on a wall in the mosque, from a page anyone can
 * open, so it is a NAME — letters (any script), with spaces, hyphens and apostrophes between them, at most 20 — never a
 * link, a number, a slogan or a bidi control. Staff can still hide one («Skjul navn»).
 */
const MAX_SHOWN_NAME = 20;
const NAME_RE = /^[\p{L}\p{M}]+(?:[ '’-][\p{L}\p{M}]+)*$/u;
/** How many of the latest gifts the page and the wall list. */
const RECENT_GIFTS = 5;

const refuse = (code, message) => ({ ok: false, code, message });

/* ---- the public page's address (2026-10-31) ---- */
/**
 * A slug from a title, the way MKK's portal makes one: lower case, Norwegian letters spelled out (æ ae, ø oe, å aa),
 * accents dropped, anything else a single hyphen, cut to MAX_SLUG. A title with no Latin letters at all (an Urdu one)
 * gives «innsamling»; uniqueSlug then numbers it.
 */
function toSlug(title) {
  const s = String(title || '').toLowerCase()
    .replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const cut = s.slice(0, MAX_SLUG).replace(/-+$/g, '');
  return cut || 'innsamling';
}
/** `base`, or base-2, base-3 … until one is not in `taken` (still within MAX_SLUG). */
function uniqueSlug(base, taken = []) {
  const set = new Set(taken || []);
  if (!set.has(base)) return base;
  for (let n = 2; n < 100000; n++) {
    const s = `${base.slice(0, MAX_SLUG - String(n).length - 1).replace(/-+$/g, '')}-${n}`;
    if (!set.has(s)) return s;
  }
  return null;
}
const isSlug = (s) => typeof s === 'string' && s.length > 0 && s.length <= MAX_SLUG && SLUG_RE.test(s);
/** The public page: `<base>/v1/gi/<slug>` — by the slug when the campaign has one (older rows get theirs on the next edit), else the id. */
function pageUrl(base, c) {
  if (!c || !base) return null;
  return `${String(base).replace(/\/+$/, '')}/v1/gi/${encodeURIComponent(c.slug || c.id)}`;
}

/* ---- the giver's choice to be named (2026-10-31) ---- */
/**
 * What the gift form sends about the giver: `showName` (the tick «Vis fornavnet mitt») and, when ticked, `name` — the
 * first name as they typed it, one line, at most MAX_SHOWN_NAME. Not ticked: nothing is kept (shownName null).
 * → { ok, value: { showName, shownName } } | { ok: false, errors }
 */
function giverInput(input) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const show = src.showName;
  if (show !== undefined && show !== null && typeof show !== 'boolean') return { ok: false, errors: { showName: '«Vis fornavnet mitt» er på (true) eller av (false).' } };
  if (!show) return { ok: true, value: { showName: false, shownName: null } };
  const name = oneLine(src.name, 'fornavnet ditt', MAX_SHOWN_NAME);
  if (!name.ok) return { ok: false, errors: { name: name.message } };
  if (!NAME_RE.test(name.value)) return { ok: false, errors: { name: 'Skriv bare fornavnet ditt, med bokstaver.' } };
  return { ok: true, value: { showName: true, shownName: name.value } };
}
/**
 * «Siste gaver»: the latest captured gifts (giftOre > 0) whose giver ticked «Vis fornavnet mitt», newest first, at most
 * `limit` — the first name and the amount. A gift whose giver did not tick it is NEVER listed on its own (PM
 * 2026-10-31: «En giver 500 kr» seconds after a payment can point at the one person who just paid); it only grows the
 * total. Never a reference, never a time. `rows` are donation rows (show_name, shown_name, pay_status, captured_ore,
 * refunded_ore, amount_nok, captured_at).
 */
function recentGifts(rows, { limit = RECENT_GIFTS, withIds = false } = {}) {
  return (rows || [])
    .map((r) => ({ id: r && r.id, name: r && r.show_name && r.shown_name ? String(r.shown_name) : null, amountOre: giftOre(r), at: (r && r.captured_at) || '' }))
    .filter((g) => g.amountOre > 0 && g.name)
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, Math.max(0, limit))
    .map(({ id, name, amountOre }) => (withIds ? { id, name, amountOre } : { name, amountOre }));
}
/**
 * The campaign the wall stays on: the first RUNNING one in telethon mode TODAY, or null. `today` for raw campaigns
 * (phase and liveOn by the mosque's day); without it the items' public view is read, where `live` is already today's.
 */
function telethon(list, today) {
  return (list || []).find((c) => c && (today ? liveToday(c, today) && open(c, today) : c.live === true && c.status === 'running')) || null;
}

/** Whole øre from 100 (1 kr) to MAX_GOAL_ORE — never a fraction, never a string of digits. */
function goal(v) {
  if (!Number.isSafeInteger(v) || v < 100) return { ok: false, message: 'Målet må være et helt antall øre, minst 1 kr.' };
  if (v > MAX_GOAL_ORE) return { ok: false, message: `Målet kan være høyst ${formatKr(MAX_GOAL_ORE)} kr.` };
  return { ok: true, value: v };
}
/** A goal as staff type it, in whole kroner or with øre: «50 000», «12500,50». → { ok, ore } | { ok: false, message } */
function kroner(input) {
  const s = String(input ?? '').trim().replace(/[\s ]/g, '').replace(/kr$/i, '');
  const m = /^(\d{1,9})(?:[.,](\d{1,2}))?$/.exec(s);
  if (!m) return { ok: false, message: 'Skriv målet i kroner, for eksempel 50000.' };
  const ore = Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0') || 0);
  const r = goal(ore);
  return r.ok ? { ok: true, ore } : { ok: false, message: r.message };
}

/**
 * A campaign as staff send it — every field checked, unknown fields refused. `base` is the campaign as it is now for an
 * edit (only the fields sent change), nothing for a new one. `today` is the mosque's day: a new campaign, or a changed
 * last day, cannot end before today. `purposeLocked`: gifts are already tied to it, so its purpose stays (they were
 * given under it). → { ok, value, changed: [field…] } | { ok: false, errors }
 */
function campaignInput(input, { today, base = null, purposeLocked = false } = {}) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src) return { ok: false, errors: { campaign: 'Ugyldig innsamling.' } };
  const unknown = Object.keys(src).filter((k) => !FIELDS.includes(k) && !FLAGS.includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const cur = base || { title: undefined, text: null, purpose: undefined, goalOre: undefined, startsOn: undefined, endsOn: undefined, status: 'draft', live: false, showDonors: false, eventId: null };
  const has = (k) => Object.prototype.hasOwnProperty.call(src, k) && src[k] !== undefined;
  const pick = (k) => (has(k) ? src[k] : cur[k]);
  const errors = {}, out = {};
  const take = (k, r) => { if (r.ok) out[k] = r.value; else errors[k] = r.message; };
  take('title', oneLine(pick('title'), 'tittelen', MAX_TITLE));
  take('text', text(pick('text'), 'teksten', MAX_TEXT));
  const purpose = pick('purpose');
  if (!PURPOSES.includes(purpose)) errors.purpose = 'Velg et formål.';
  else if (base && purposeLocked && purpose !== base.purpose) errors.purpose = 'Formålet kan ikke endres etter at noen har gitt til innsamlingen.';
  else out.purpose = purpose;
  take('goalOre', goal(pick('goalOre')));
  const startsOn = pick('startsOn'), endsOn = pick('endsOn');
  if (!Day.valid(startsOn)) errors.startsOn = 'Første dag må være en gyldig dato (ÅÅÅÅ-MM-DD).'; else out.startsOn = startsOn;
  if (!Day.valid(endsOn)) errors.endsOn = 'Siste dag må være en gyldig dato (ÅÅÅÅ-MM-DD).';
  else if (out.startsOn && Day.diff(out.startsOn, endsOn) < 0) errors.endsOn = 'Siste dag kan ikke være før første dag.';
  else if (out.startsOn && Day.diff(out.startsOn, endsOn) > MAX_DAYS) errors.endsOn = 'En innsamling kan vare høyst to år.';
  else if ((!base || endsOn !== base.endsOn) && today && Day.valid(today) && Day.diff(today, endsOn) < 0) errors.endsOn = 'Siste dag kan ikke være passert.';
  else out.endsOn = endsOn;
  const status = pick('status');
  if (!SETTABLE_STATUSES.includes(status)) errors.status = 'Status kan være utkast eller aktiv. Avslutt med «Avslutt».';
  else out.status = status;
  // The telethon switches (2026-10-31): off unless sent; an edit keeps the campaign's own; anything but a boolean is refused.
  for (const k of FLAGS) {
    const v = pick(k);
    if (v === undefined || v === null) out[k] = false;
    else if (typeof v !== 'boolean') errors[k] = FLAG_NO[k];
    else out[k] = v;
  }
  const eventId = pick('eventId') ?? null;
  if (eventId !== null && !isId(eventId)) errors.eventId = 'Ugyldig arrangement.'; else out.eventId = eventId;
  if (Object.keys(errors).length) return { ok: false, errors };
  // Telethon mode is for a day (PM 2026-10-31): `live` SENT as true starts it today (liveOn = today) — also when it was
  // left on from an earlier day; sent false ends it (liveOn null). Not sent: the campaign's own live and liveOn stay.
  const wasToday = Boolean(base && liveToday(base, today));
  // Switched ON: only while the campaign runs today (a «today» set on a scheduled campaign would be lost by its first
  // day), and only with a valid day to store (review 2026-10-31).
  if (has('live') && out.live && !wasToday && !(Day.valid(today) && phase(out, today) === 'running')) {
    return { ok: false, errors: { live: 'Telethon-modus kan bare slås på mens innsamlingen pågår (aktiv, og i dag innenfor periodens dager).' } };
  }
  if (has('live')) out.liveOn = out.live ? (wasToday ? base.liveOn : (Day.valid(today) ? today : null)) : null;
  else out.liveOn = base ? (base.liveOn ?? null) : null;
  const liveChanged = has('live') && (base ? (out.live ? !wasToday : Boolean(base.live)) : out.live);
  const changed = base
    ? [...FIELDS.filter((k) => (base[k] ?? null) !== (out[k] ?? null)), ...(liveChanged ? ['live'] : []), ...(Boolean(base.showDonors) !== out.showDonors ? ['showDonors'] : [])]
    : [...FIELDS.filter((k) => out[k] != null), ...FLAGS.filter((k) => out[k] === true)];
  return { ok: true, value: out, changed };
}

/** Whether staff may still change it: anything but closed. */
const editable = (c) => Boolean(c && c.status !== 'closed');

/** Closing: final, from draft or active. → { ok, value: { status: 'closed' } } | refusal */
function closing(c) {
  if (!c) return refuse('not_found', 'Fant ikke innsamlingen.');
  if (c.status === 'closed') return refuse('campaign_closed', 'Innsamlingen er allerede avsluttet.');
  return { ok: true, value: { status: 'closed' } };
}

/** Where the campaign is today: draft | scheduled | running | ended | closed. Only `running` is shown to givers. */
function phase(c, today) {
  if (!c) return null;
  if (c.status === 'closed') return 'closed';
  if (c.status === 'draft') return 'draft';
  if (Day.valid(today) && Day.diff(today, c.startsOn) > 0) return 'scheduled';
  if (Day.valid(today) && Day.diff(today, c.endsOn) < 0) return 'ended';
  return 'running';
}
/** Whether a gift may be tied to it now. */
const open = (c, today) => phase(c, today) === 'running';
/** Days after today until the last day (0 = today is the last day), while it runs; else null. */
const daysLeft = (c, today) => (phase(c, today) === 'running' ? Math.max(0, Day.diff(today, c.endsOn)) : null);

/**
 * What a Vipps gift row counts for, in øre: captured, net of refunds; nothing for anything else. Mirrors
 * vipps-payment.shownAmountNok + givenGift: refunded (in full) → 0; captured with a captured amount → captured − refunded;
 * captured without one (an older row) → amount_nok in øre.
 */
function giftOre(row) {
  if (!row || row.pay_status !== 'captured') return 0;
  const cap = Number(row.captured_ore || 0), ref = Number(row.refunded_ore || 0);
  if (cap > 0) return Math.max(0, cap - ref);
  const nok = Number(row.amount_nok || 0);
  return Number.isFinite(nok) && nok > 0 ? Math.round(nok * 100) : 0;
}

/**
 * Other gifts' totals, as another module hands them in (cash gifts: [{ campaignId, amountOre, count, month?, source }]).
 * A malformed entry is left out, never guessed at: whole non-negative øre and count, a known campaign id.
 */
function externalEntries(external) {
  if (!Array.isArray(external)) return [];
  return external.filter((e) => e && typeof e === 'object' && typeof e.campaignId === 'string' && e.campaignId
    && Number.isSafeInteger(e.amountOre) && e.amountOre >= 0 && Number.isSafeInteger(e.count) && e.count >= 0
    && (e.month === undefined || e.month === null || /^\d{4}-(0[1-9]|1[0-2])$/.test(String(e.month))));
}

/**
 * What each campaign has raised: { [id]: { raisedOre, giftCount, vippsOre, vippsCount, externalOre, externalCount } }.
 * `gifts` are donation rows with campaign_id (and pay_status, captured_ore, refunded_ore, amount_nok); `external` as
 * externalEntries. Every id asked for is in the answer, with zeros when nothing came in.
 */
function tally(ids, { gifts = [], external = [] } = {}) {
  const out = {};
  for (const id of ids || []) out[id] = { raisedOre: 0, giftCount: 0, vippsOre: 0, vippsCount: 0, externalOre: 0, externalCount: 0 };
  for (const g of gifts || []) {
    const t = g && out[g.campaign_id];
    if (!t) continue;
    const ore = giftOre(g);
    if (ore <= 0) continue;
    t.vippsOre += ore; t.vippsCount += 1;
  }
  for (const e of externalEntries(external)) {
    const t = out[e.campaignId];
    if (!t) continue;
    t.externalOre += e.amountOre; t.externalCount += e.count;
  }
  for (const t of Object.values(out)) { t.raisedOre = t.vippsOre + t.externalOre; t.giftCount = t.vippsCount + t.externalCount; }
  return out;
}

/** Whole per cent of the goal reached (may pass 100); the bar is clamped by barPercent. */
const percent = (raisedOre, goalOre) => (goalOre > 0 ? Math.floor((Math.max(0, raisedOre) * 100) / goalOre) : 0);
const barPercent = (p) => Math.max(0, Math.min(100, Number(p) || 0));

/** A campaign as staff see it. */
function staffView(c, totals, today) {
  const t = totals || { raisedOre: 0, giftCount: 0, vippsOre: 0, vippsCount: 0, externalOre: 0, externalCount: 0 };
  return { id: c.id, title: c.title, text: c.text, purpose: c.purpose, donationPurpose: DONATION_PURPOSE[c.purpose] || 'general',
    goalOre: c.goalOre, startsOn: c.startsOn, endsOn: c.endsOn, status: c.status, phase: phase(c, today), daysLeft: daysLeft(c, today),
    raisedOre: t.raisedOre, giftCount: t.giftCount, vippsOre: t.vippsOre, vippsCount: t.vippsCount, externalOre: t.externalOre,
    externalCount: t.externalCount, percent: percent(t.raisedOre, c.goalOre), closedAt: c.closedAt || null,
    // Telethon (2026-10-31): the page's slug (null on an older row until its next edit) and the two switches.
    slug: c.slug || null, live: Boolean(c.live), liveOn: c.liveOn || null, liveToday: liveToday(c, today), showDonors: Boolean(c.showDonors),
    createdAt: c.createdAt, updatedAt: c.updatedAt, version: c.version, eventId: c.eventId || null };
}

/** The mosque-local day a closed campaign was closed on (from closedAt, an instant), else null. */
function closedOn(c, zone) {
  if (!c || c.status !== 'closed' || !c.closedAt) return null;
  const ms = Date.parse(c.closedAt);
  return Number.isFinite(ms) ? tz.dateIn(new Date(ms), zone) : null;
}
/**
 * Whether a closed campaign was ever before the public: its first day had come by the day it was closed, and at least
 * one gift counts for it (`totals` as tally). A draft closed early, or a campaign nobody gave to, is not a «result».
 */
function wasLive(c, totals, zone) {
  const d = closedOn(c, zone);
  return Boolean(d && Day.valid(c.startsOn) && Day.diff(c.startsOn, d) >= 0 && totals && totals.giftCount > 0);
}
/**
 * Closed campaigns whose final total is public today: closed on one of the last `days` days (0 = today) and wasLive.
 * The most recently closed first. `totals` as tally (needed for wasLive).
 */
function recentlyClosed(campaigns, totals, today, zone, days = CLOSED_PUBLIC_DAYS) {
  if (!Day.valid(today)) return [];
  return (campaigns || []).filter((c) => {
    const d = closedOn(c, zone);
    return d && Day.diff(d, today) >= 0 && Day.diff(d, today) <= days && wasLive(c, (totals || {})[c.id], zone);
  }).sort((a, b) => closedOn(b, zone).localeCompare(closedOn(a, zone)) || a.title.localeCompare(b.title, 'nb') || a.id.localeCompare(b.id));
}

/**
 * A campaign as everybody sees it (app, gift page, screens): no status internals, no staff, no donor — a total and a
 * count. `status`: 'running' (takes gifts) or 'closed' (its final total, `closedOn` the day; takes nothing).
 */
function publicView(c, totals, today, zone, { recentGifts: recent = [] } = {}) {
  const t = totals || { raisedOre: 0, giftCount: 0 };
  const closed = c.status === 'closed';
  return { id: c.id, title: c.title, text: c.text || null, purpose: c.purpose, donationPurpose: DONATION_PURPOSE[c.purpose] || 'general',
    goalOre: c.goalOre, raisedOre: t.raisedOre, giftCount: t.giftCount, percent: percent(t.raisedOre, c.goalOre),
    startsOn: c.startsOn, endsOn: c.endsOn, daysLeft: closed ? null : daysLeft(c, today),
    status: closed ? 'closed' : 'running', closedOn: closed ? closedOn(c, zone) : null,
    // Telethon (2026-10-31): the page's slug, the switches, and — only when the mosque shows givers — the latest gifts
    // as recentGifts() made them (a first name or null, an amount). A closed campaign is never live.
    slug: c.slug || null, live: liveToday(c, today) && !closed, showDonors: Boolean(c.showDonors),
    recentGifts: c.showDonors ? (recent || []) : [] };
}

/** Running campaigns, the one ending soonest first, then by title. */
function running(campaigns, today) {
  return (campaigns || []).filter((c) => open(c, today))
    .sort((a, b) => a.endsOn.localeCompare(b.endsOn) || a.title.localeCompare(b.title, 'nb') || a.id.localeCompare(b.id));
}

/** Staff's list: running, then scheduled, drafts, ended, closed; within each the newest last day first. */
const PHASE_ORDER = { running: 0, scheduled: 1, draft: 2, ended: 3, closed: 4 };
function sort(campaigns, today) {
  return [...(campaigns || [])].sort((a, b) => PHASE_ORDER[phase(a, today)] - PHASE_ORDER[phase(b, today)]
    || b.endsOn.localeCompare(a.endsOn) || a.title.localeCompare(b.title, 'nb') || a.id.localeCompare(b.id));
}

/** The report's year: ?year=YYYY, or this year. */
function reportYear(q = {}, today) {
  const y = q.year === undefined || q.year === null || q.year === '' ? String(today || '').slice(0, 4) : String(q.year);
  if (!/^\d{4}$/.test(y) || Number(y) < 2000 || Number(y) > 2199) return { ok: false, message: 'Oppgi året som fire sifre, for eksempel 2026.' };
  return { ok: true, value: y };
}

/**
 * The monthly report: per campaign and month (mosque-local, from when the gift was captured), what came in through Vipps
 * and through the other sources. Rows only for months where something came in. `campaigns` are the rows staff see;
 * `gifts` donation rows (campaign_id, captured_at, started_at, …); `external` as externalEntries — an entry without a
 * month is counted in the year's totals of the campaign but cannot be placed in a month (month '').
 * → [{ campaignId, title, purpose, month, vippsOre, vippsCount, externalOre, externalCount, totalOre }]
 */
function monthly(campaigns, { gifts = [], external = [], zone, year } = {}) {
  const byId = new Map((campaigns || []).map((c) => [c.id, c]));
  const rows = new Map();
  const row = (id, month) => {
    const k = `${id}\n${month}`;
    if (!rows.has(k)) {
      const c = byId.get(id);
      rows.set(k, { campaignId: id, title: c.title, purpose: c.purpose, month, vippsOre: 0, vippsCount: 0, externalOre: 0, externalCount: 0, totalOre: 0 });
    }
    return rows.get(k);
  };
  for (const g of gifts || []) {
    if (!g || !byId.has(g.campaign_id)) continue;
    const ore = giftOre(g);
    if (ore <= 0) continue;
    const at = g.captured_at || g.started_at;
    const ms = Date.parse(at);
    if (!Number.isFinite(ms)) continue;
    const month = tz.dateIn(new Date(ms), zone).slice(0, 7);
    if (year && month.slice(0, 4) !== String(year)) continue;
    const r = row(g.campaign_id, month);
    r.vippsOre += ore; r.vippsCount += 1;
  }
  for (const e of externalEntries(external)) {
    if (!byId.has(e.campaignId)) continue;
    const month = e.month ? String(e.month) : '';
    if (year && month && month.slice(0, 4) !== String(year)) continue;
    if (e.amountOre === 0 && e.count === 0) continue;
    const r = row(e.campaignId, month);
    r.externalOre += e.amountOre; r.externalCount += e.count;
  }
  const out = [...rows.values()];
  for (const r of out) r.totalOre = r.vippsOre + r.externalOre;
  return out.sort((a, b) => a.title.localeCompare(b.title, 'nb') || a.campaignId.localeCompare(b.campaignId) || a.month.localeCompare(b.month));
}

/**
 * The report as CSV for a spreadsheet (csv.js): semicolons (Norwegian Excel), CRLF, kroner with a comma, no grouping. A
 * title that starts like a formula gets a leading apostrophe (staff typed it; the accountant opens the file). The money
 * columns are ours (Csv.num): a negative sum — a cash correction larger than the gifts — stays «-5,00», a number, never
 * «'-5,00» (fixed 2026-10-29: the money went through the typed-text escaping before).
 */
const CSV_HEAD = ['Innsamling', 'Formål', 'Måned', 'Vipps (kr)', 'Gaver med Vipps', 'Andre gaver (kr)', 'Andre gaver (antall)', 'Sum (kr)'];
function csv(rows) {
  const money = (ore) => Csv.num(formatKr(ore, { grouped: false }));
  const t = Csv.cell;
  const lines = (rows || []).map((r) => Csv.line([t(r.title), t(PURPOSE_NO[r.purpose] || r.purpose), t(r.month || 'uten måned'), money(r.vippsOre), t(r.vippsCount),
    money(r.externalOre), t(r.externalCount), money(r.totalOre)]));
  return Csv.table(CSV_HEAD, lines);
}

/** The activity log's fields (audit.campaignAudit keeps only these): what the campaign is, never who gave. */
function auditView(c = {}) {
  const out = {};
  if (c.title !== undefined) out.campaign_title = c.title;
  if (c.purpose !== undefined) out.campaign_purpose = c.purpose;
  if (c.goalOre !== undefined) out.campaign_goal_ore = c.goalOre;
  if (c.startsOn !== undefined) out.campaign_starts_on = c.startsOn;
  if (c.endsOn !== undefined) out.campaign_ends_on = c.endsOn;
  if (c.status !== undefined) out.campaign_status = c.status;
  // Telethon (2026-10-31): the switches and the slug are operational — what the page is called, never who gave.
  if (c.live !== undefined) out.campaign_live = Boolean(c.live);
  if (c.liveOn) out.campaign_live_on = c.liveOn;
  if (c.showDonors !== undefined) out.campaign_show_donors = Boolean(c.showDonors);
  if (c.slug !== undefined && c.slug !== null) out.campaign_slug = c.slug;
  return out;
}

module.exports = {
  PURPOSES, PURPOSE_NO, DONATION_PURPOSE, CLOSED_PUBLIC_DAYS, CLOSED_SCREEN_DAYS, STATUSES, STATUS_NO, SETTABLE_STATUSES, PHASE_NO, MAX_TITLE, MAX_TEXT, MAX_GOAL_ORE, MAX_DAYS,
  MAX_CAMPAIGNS, FIELDS, FLAGS, CSV_HEAD, MAX_SLUG, MAX_SHOWN_NAME, RECENT_GIFTS,
  isId, goal, kroner, kr, formatKr, campaignInput, editable, closing, phase, open, daysLeft, giftOre, externalEntries, tally,
  percent, barPercent, staffView, publicView, closedOn, wasLive, recentlyClosed, running, sort, reportYear, monthly, csv, auditView,
  toSlug, uniqueSlug, isSlug, pageUrl, giverInput, recentGifts, telethon, liveToday,
};
